import { useState } from 'react';
import { POST_SELF_EDIT_WINDOW_LABEL } from '@/lib/post-edit-window';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { FormSubmitOverlay } from '@/components/forms/form-submit-overlay';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormRadioGroupField } from '@/components/forms/form-radio-group-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  MeetupForm,
  type MeetupEditTarget,
  type MeetupFormValues,
} from '@/pages/create/meetup/meetup-form';
import type {
  LeadPostData,
  MeetupPostData,
  SocialPost,
  TestimonialPostData,
  ThankYouPostData,
} from '../types';

const THANK_YOU_FREQUENCY_OPTIONS = [
  { value: 'One Time', label: 'One Time' },
  { value: 'Monthly', label: 'Monthly' },
  { value: '6 Monthly', label: '6 Monthly' },
  { value: 'Yearly', label: 'Yearly' },
  { value: 'Other', label: 'Other' },
];

const THANK_YOU_FREQUENCY_UNIT_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'Week', label: 'Week' },
  { value: 'Month', label: 'Month' },
  { value: 'Year', label: 'Year' },
];

const URGENCY_OPTIONS = ['Now', '15 Days', '3 Months', '6 Months', 'Likely', 'Relational'].map(
  (value) => ({ value, label: value })
);

const EDIT_WINDOW_NOTE = `The recipient can't be changed. You can edit this post for ${POST_SELF_EDIT_WINDOW_LABEL} after posting.`;

const thankYouEditSchema = z
  .object({
    amount: z.string().min(1, 'Amount is required'),
    frequencySelection: z.enum(['One Time', 'Monthly', '6 Monthly', 'Yearly', 'Other']),
    otherFrequencyNumber: z.string().optional(),
    otherFrequencyUnit: z.enum(['Day', 'Week', 'Month', 'Year']).optional(),
    message: z
      .string()
      .min(10, 'Please write a heartfelt message')
      .max(500, 'Message cannot exceed 500 characters'),
  })
  .superRefine((data, ctx) => {
    if (data.frequencySelection === 'Other') {
      if (!data.otherFrequencyNumber) {
        ctx.addIssue({
          code: 'custom',
          message: 'Number is required',
          path: ['otherFrequencyNumber'],
        });
      }
      if (!data.otherFrequencyUnit) {
        ctx.addIssue({
          code: 'custom',
          message: 'Unit is required',
          path: ['otherFrequencyUnit'],
        });
      }
    }
  });

const leadEditSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  requirement: z
    .string()
    .min(10, 'Please describe the requirement in detail')
    .max(1000, 'Requirement cannot exceed 1000 characters'),
  urgency: z.string().min(1, 'Please select when this is required'),
});

const testimonialEditSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  notes: z
    .string()
    .min(10, 'Please describe the service in detail')
    .max(1000, 'Description cannot exceed 1000 characters'),
});

type ThankYouEditValues = z.infer<typeof thankYouEditSchema>;
type LeadEditValues = z.infer<typeof leadEditSchema>;
type TestimonialEditValues = z.infer<typeof testimonialEditSchema>;

const MEETING_LOCATION_OTHER = 'OTHER';

const getMeetupInitialValues = (post: MeetupPostData): MeetupFormValues => {
  const isOtherLocation = post.meetupLocation === 'Other';
  return {
    initiatorId: post.initiatorId,
    participantIds: (post.meetupParticipants ?? [])
      .map((participant) => participant.id)
      .filter((id) => id !== post.initiatorId),
    type: post.meetupType,
    meetingMode: post.meetupMode,
    location: isOtherLocation ? MEETING_LOCATION_OTHER : (post.meetupLocation ?? ''),
    otherLocation: post.meetupOtherLocation ?? '',
    date: post.meetupDate.slice(0, 10),
    time: post.meetupTime ?? '',
    duration: post.meetupDuration ?? '',
    message: post.meetupMessage ?? '',
    powerVisitDetails: post.powerVisitDetails ?? '',
  };
};

const formatAmount = (value: string) => {
  const rawValue = value.replace(/[^0-9]/g, '');
  return rawValue ? parseInt(rawValue, 10).toLocaleString('en-IN') : '';
};

const getThankYouFrequency = (
  businessFrequencyTime: string
): Pick<
  ThankYouEditValues,
  'frequencySelection' | 'otherFrequencyNumber' | 'otherFrequencyUnit'
> => {
  const defaultOther = { otherFrequencyNumber: '', otherFrequencyUnit: 'Month' as const };
  if (!businessFrequencyTime) {
    return { frequencySelection: 'One Time', ...defaultOther };
  }
  const preset = THANK_YOU_FREQUENCY_OPTIONS.find(
    (option) => option.value !== 'Other' && option.value === businessFrequencyTime
  );
  if (preset) {
    return {
      frequencySelection: preset.value as ThankYouEditValues['frequencySelection'],
      ...defaultOther,
    };
  }
  const match = businessFrequencyTime.match(/^(\d+)\s+(Day|Week|Month|Year)$/i);
  if (match) {
    const unit = THANK_YOU_FREQUENCY_UNIT_OPTIONS.find(
      (option) => option.value.toLowerCase() === match[2].toLowerCase()
    );
    return {
      frequencySelection: 'Other',
      otherFrequencyNumber: match[1],
      otherFrequencyUnit: (unit?.value ?? 'Month') as ThankYouEditValues['otherFrequencyUnit'],
    };
  }
  return { frequencySelection: 'Other', ...defaultOther };
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    return (error.response?.data as { error?: string } | undefined)?.error;
  }
  return undefined;
};

interface PostEditFormProps {
  post: SocialPost;
  onCancel: () => void;
  onSaved: (postId: string) => void;
}

export function PostEditForm({ post, onCancel, onSaved }: PostEditFormProps) {
  if (post.type === 'THANK_YOU') {
    return <ThankYouEditForm post={post} onCancel={onCancel} onSaved={onSaved} />;
  }
  if (post.type === 'LEAD') {
    return <LeadEditForm post={post} onCancel={onCancel} onSaved={onSaved} />;
  }
  if (post.type === 'TESTIMONIAL') {
    return <TestimonialEditForm post={post} onCancel={onCancel} onSaved={onSaved} />;
  }
  if (post.type === 'MEETUP') {
    return <MeetupEditForm post={post} onCancel={onCancel} onSaved={onSaved} />;
  }
  return null;
}

interface EditFormProps<T> {
  post: T;
  onCancel: () => void;
  onSaved: (postId: string) => void;
}

interface EditFormCardProps {
  title: string;
  description: string;
  isSaving: boolean;
  children: React.ReactNode;
}

function EditFormCard({ title, description, isSaving, children }: EditFormCardProps) {
  return (
    <>
      <FormSubmitOverlay
        open={isSaving}
        title="Saving changes..."
        description="Saving your updates."
      />
      <Card className="mx-auto w-full max-w-5xl animate-in slide-in-from-bottom-5 border-0 bg-background/50 shadow-none backdrop-blur-md duration-300 md:border md:border-border md:shadow-2xl">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-2xl font-black tracking-tight text-foreground">
            {title}
          </CardTitle>
          <CardDescription className="text-muted-foreground">{description}</CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">{children}</CardContent>
      </Card>
    </>
  );
}

interface SubmitButtonsProps {
  isSaving: boolean;
  onCancel: () => void;
}

function SubmitButtons({ isSaving, onCancel }: SubmitButtonsProps) {
  return (
    <div className="flex flex-col-reverse justify-end gap-3 border-t border-border pt-6 md:flex-row">
      <Button
        type="button"
        variant="ghost"
        className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
        onClick={onCancel}
        disabled={isSaving}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        disabled={isSaving}
        className="w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
      >
        {isSaving ? 'Saving...' : 'Save Changes'}
      </Button>
    </div>
  );
}

function MeetupEditForm({ post, onCancel, onSaved }: EditFormProps<MeetupPostData>) {
  const editing: MeetupEditTarget = {
    postId: post.id,
    communityId: post.communityId,
    initialValues: getMeetupInitialValues(post),
  };

  return <MeetupForm editing={editing} onCancel={onCancel} onSubmit={onSaved} />;
}

function TestimonialEditForm({ post, onCancel, onSaved }: EditFormProps<TestimonialPostData>) {
  const [isSaving, setIsSaving] = useState(false);
  const form = useForm<TestimonialEditValues>({
    resolver: zodResolver(testimonialEditSchema),
    defaultValues: {
      title: post.testimonialTitle,
      notes: post.testimonialContent,
    },
  });

  const onSubmit = async (values: TestimonialEditValues) => {
    setIsSaving(true);
    try {
      await axiosInstance.patch(`/api/posts/${post.id}`, values);
      toast.success('Testimonial updated');
      onSaved(post.id);
    } catch (error) {
      console.error('Edit testimonial failed:', error);
      toast.error(getErrorMessage(error) ?? 'Failed to update testimonial');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditFormCard title="Edit Testimonial" description={EDIT_WINDOW_NOTE} isSaving={isSaving}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormInputField
            control={form.control}
            name="title"
            label="Title"
            placeholder="e.g., Excellent Web Design Service"
            required
          />
          <FormTextareaField
            control={form.control}
            name="notes"
            label="What services were offered and how were you benefited?"
            placeholder="Describe your experience... Use @ for a member or business, # for a category"
            maxLength={1000}
            required
            mentions
          />
          <SubmitButtons isSaving={isSaving} onCancel={onCancel} />
        </form>
      </Form>
    </EditFormCard>
  );
}

function ThankYouEditForm({ post, onCancel, onSaved }: EditFormProps<ThankYouPostData>) {
  const [isSaving, setIsSaving] = useState(false);
  const form = useForm<ThankYouEditValues>({
    resolver: zodResolver(thankYouEditSchema),
    defaultValues: {
      amount: Number(post.thankYouAmount).toLocaleString('en-IN'),
      message: post.thankYouMessage,
      ...getThankYouFrequency(post.businessFrequencyTime),
    },
  });
  const frequencySelection = useWatch({ control: form.control, name: 'frequencySelection' });

  const onSubmit = async (values: ThankYouEditValues) => {
    setIsSaving(true);
    try {
      const isRecurring = values.frequencySelection !== 'One Time';
      await axiosInstance.patch(`/api/posts/${post.id}`, {
        amount: Number(values.amount.replace(/,/g, '')),
        frequency: isRecurring ? 'RECURRING' : 'ONE_TIME',
        recurringDuration: isRecurring
          ? values.frequencySelection === 'Other'
            ? `${values.otherFrequencyNumber} ${values.otherFrequencyUnit}`
            : values.frequencySelection
          : undefined,
        message: values.message,
      });
      toast.success('Thank you note updated');
      onSaved(post.id);
    } catch (error) {
      console.error('Edit thank you note failed:', error);
      toast.error(getErrorMessage(error) ?? 'Failed to update thank you note');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditFormCard title="Edit Thank You Note" description={EDIT_WINDOW_NOTE} isSaving={isSaving}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Amount of Business Transaction <span className="text-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder="0.00"
                    icon="₹"
                    onChange={(event) => field.onChange(formatAmount(event.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormRadioGroupField
            control={form.control}
            name="frequencySelection"
            label="Business Frequency"
            required
            layout="row"
            options={THANK_YOU_FREQUENCY_OPTIONS}
          />
          {frequencySelection === 'Other' && (
            <div className="ml-1 flex gap-4 border-l-2 border-primary/20 pl-4">
              <FormInputField
                control={form.control}
                name="otherFrequencyNumber"
                label="Number"
                placeholder="e.g. 1"
                type="number"
                required
              />
              <FormSelectField
                control={form.control}
                name="otherFrequencyUnit"
                label="Unit"
                placeholder="Select unit"
                options={THANK_YOU_FREQUENCY_UNIT_OPTIONS}
                required
              />
            </div>
          )}
          <FormTextareaField
            control={form.control}
            name="message"
            label="What message would you like to give?"
            placeholder="Write your note here... Use @ for a member or business, # for a category"
            maxLength={500}
            required
            mentions
          />
          <SubmitButtons isSaving={isSaving} onCancel={onCancel} />
        </form>
      </Form>
    </EditFormCard>
  );
}

function LeadEditForm({ post, onCancel, onSaved }: EditFormProps<LeadPostData>) {
  const [isSaving, setIsSaving] = useState(false);
  const form = useForm<LeadEditValues>({
    resolver: zodResolver(leadEditSchema),
    defaultValues: {
      title: post.leadTitle,
      requirement: post.leadDescription,
      urgency: post.leadTimeline,
    },
  });

  const requirementLabel =
    post.leadType === 'SELF'
      ? 'What product/service do you want to avail?'
      : `What product/service does ${post.leadRecipientFirstName || 'they'} want to avail?`;

  const onSubmit = async (values: LeadEditValues) => {
    setIsSaving(true);
    try {
      await axiosInstance.patch(`/api/posts/${post.id}`, values);
      toast.success('Lead updated');
      onSaved(post.id);
    } catch (error) {
      console.error('Edit lead failed:', error);
      toast.error(getErrorMessage(error) ?? 'Failed to update lead');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditFormCard title="Edit Business Lead" description={EDIT_WINDOW_NOTE} isSaving={isSaving}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormInputField
            control={form.control}
            name="title"
            label="Title"
            placeholder="e.g., Need 50 Office Chairs"
            required
          />
          <FormTextareaField
            control={form.control}
            name="requirement"
            label={requirementLabel}
            placeholder="Describe the requirement... Use @ for a member or business, # for a category"
            maxLength={1000}
            required
            mentions
          />
          <FormRadioGroupField
            control={form.control}
            name="urgency"
            label="When is the product/service required?"
            required
            layout="scroll-x"
            options={URGENCY_OPTIONS}
          />
          <SubmitButtons isSaving={isSaving} onCancel={onCancel} />
        </form>
      </Form>
    </EditFormCard>
  );
}
